import { type ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import AudiotrackOutlinedIcon from '@mui/icons-material/AudiotrackOutlined';
import ContentCutOutlinedIcon from '@mui/icons-material/ContentCutOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import GraphicEqOutlinedIcon from '@mui/icons-material/GraphicEqOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import {
    Alert,
    Box,
    Button,
    Chip,
    Divider,
    LinearProgress,
    MenuItem,
    Paper,
    Slider,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import { Title, useNotify } from 'react-admin';

const directTtsBaseUrl = 'http://localhost:9103';
const proxyTtsBaseUrl = '/tts-api';
const defaultTtsBaseUrl = import.meta.env.DEV ? proxyTtsBaseUrl : directTtsBaseUrl;
const settingsStorageKey = 'pteultra-admin:audio-generate-settings';
const historyStorageKey = 'pteultra-admin:audio-generate-history';
const maxHistoryItems = 5;

type AudioGenerateSettings = {
    baseUrl: string;
    voiceId: string;
    voiceLabel: string;
    genderCode: string;
    language: string;
    referenceText: string;
};

type AudioHistoryItem = {
    id: string;
    createdAt: string;
    fileName: string;
    text: string;
    voiceLabel: string;
    language: string;
    sizeBytes: number;
    mimeType: string;
    dataUrl: string;
};

type CutAudioOutput = {
    dataUrl: string;
    fileName: string;
    sizeBytes: number;
    durationSeconds: number;
};

type WindowWithWebkitAudioContext = Window & typeof globalThis & {
    webkitAudioContext?: typeof AudioContext;
};

const defaultSettings: AudioGenerateSettings = {
    baseUrl: defaultTtsBaseUrl,
    voiceId: 'male_1',
    voiceLabel: 'Male One',
    genderCode: 'male',
    language: 'en',
    referenceText: 'Hello, this is a cloned voice sample sent inline.',
};

function trimTrailingSlash(value: string): string {
    return value.trim().replace(/\/+$/, '');
}

function asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function asString(value: unknown, fallback = ''): string {
    return typeof value === 'string' ? value : fallback;
}

function readSettings(): AudioGenerateSettings {
    if (typeof window === 'undefined') return defaultSettings;

    try {
        const parsed = JSON.parse(window.localStorage.getItem(settingsStorageKey) ?? '{}') as Partial<AudioGenerateSettings>;
        return {
            baseUrl: import.meta.env.DEV && parsed.baseUrl === directTtsBaseUrl
                ? proxyTtsBaseUrl
                : asString(parsed.baseUrl, defaultSettings.baseUrl),
            voiceId: asString(parsed.voiceId, defaultSettings.voiceId),
            voiceLabel: asString(parsed.voiceLabel, defaultSettings.voiceLabel),
            genderCode: asString(parsed.genderCode, defaultSettings.genderCode),
            language: asString(parsed.language, defaultSettings.language),
            referenceText: asString(parsed.referenceText, defaultSettings.referenceText),
        };
    } catch {
        return defaultSettings;
    }
}

function writeSettings(settings: AudioGenerateSettings): void {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(settingsStorageKey, JSON.stringify(settings));
}

function readHistory(): AudioHistoryItem[] {
    if (typeof window === 'undefined') return [];

    try {
        const parsed = JSON.parse(window.localStorage.getItem(historyStorageKey) ?? '[]') as unknown;
        if (!Array.isArray(parsed)) return [];
        return parsed.map((item) => {
            const record = asRecord(item);
            return {
                id: asString(record.id),
                createdAt: asString(record.createdAt),
                fileName: asString(record.fileName, 'generated-voice.wav'),
                text: asString(record.text),
                voiceLabel: asString(record.voiceLabel),
                language: asString(record.language),
                sizeBytes: typeof record.sizeBytes === 'number' ? record.sizeBytes : 0,
                mimeType: asString(record.mimeType, 'audio/wav'),
                dataUrl: asString(record.dataUrl),
            };
        }).filter((item) => item.id && item.dataUrl);
    } catch {
        return [];
    }
}

function writeHistory(items: AudioHistoryItem[]): void {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(historyStorageKey, JSON.stringify(items.slice(0, maxHistoryItems)));
}

function blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ''));
        reader.onerror = () => reject(new Error('Could not read the generated audio response.'));
        reader.readAsDataURL(blob);
    });
}

function getErrorMessage(payload: unknown, fallback: string): string {
    if (typeof payload === 'string' && payload.trim()) return payload;
    const record = asRecord(payload);
    const message = record.detail ?? record.message ?? record.error ?? record.errorMessage;
    return typeof message === 'string' && message.trim() ? message : fallback;
}

function getFileExtension(file: File | null): string {
    if (!file?.name.includes('.')) return 'wav';
    return file.name.split('.').pop()?.toLowerCase() || 'wav';
}

function formatBytes(value: number): string {
    if (!Number.isFinite(value) || value <= 0) return '0 B';
    if (value < 1024) return `${value} B`;
    if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
    return `${(value / 1024 / 1024).toFixed(1)} MB`;
}

function formatDateTime(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
}

function buildDownloadName(item: Pick<AudioHistoryItem, 'fileName'>): string {
    return item.fileName.trim() || 'generated-voice.wav';
}

function formatSeconds(value: number): string {
    if (!Number.isFinite(value) || value < 0) return '0.00s';
    const minutes = Math.floor(value / 60);
    const seconds = value - minutes * 60;
    return minutes > 0 ? `${minutes}:${seconds.toFixed(2).padStart(5, '0')}` : `${seconds.toFixed(2)}s`;
}

function writeAscii(view: DataView, offset: number, value: string): void {
    for (let index = 0; index < value.length; index += 1) {
        view.setUint8(offset + index, value.charCodeAt(index));
    }
}

function encodeAudioBufferSegmentToWav(buffer: AudioBuffer, startSeconds: number, endSeconds: number): Blob {
    const channelCount = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    const startSample = Math.max(0, Math.floor(startSeconds * sampleRate));
    const endSample = Math.min(buffer.length, Math.ceil(endSeconds * sampleRate));
    const frameCount = Math.max(0, endSample - startSample);
    const bytesPerSample = 2;
    const dataSize = frameCount * channelCount * bytesPerSample;
    const arrayBuffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(arrayBuffer);

    writeAscii(view, 0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    writeAscii(view, 8, 'WAVE');
    writeAscii(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, channelCount, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * channelCount * bytesPerSample, true);
    view.setUint16(32, channelCount * bytesPerSample, true);
    view.setUint16(34, 16, true);
    writeAscii(view, 36, 'data');
    view.setUint32(40, dataSize, true);

    let offset = 44;
    for (let frame = 0; frame < frameCount; frame += 1) {
        for (let channel = 0; channel < channelCount; channel += 1) {
            const sample = buffer.getChannelData(channel)[startSample + frame] ?? 0;
            const clamped = Math.max(-1, Math.min(1, sample));
            view.setInt16(offset, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true);
            offset += bytesPerSample;
        }
    }

    return new Blob([arrayBuffer], { type: 'audio/wav' });
}

function getAudioContextConstructor(): typeof AudioContext | null {
    if (typeof window === 'undefined') return null;
    return window.AudioContext ?? (window as WindowWithWebkitAudioContext).webkitAudioContext ?? null;
}

function drawWaveform(
    canvas: HTMLCanvasElement,
    buffer: AudioBuffer,
    startSeconds: number,
    endSeconds: number,
): void {
    const context = canvas.getContext('2d');
    if (!context) return;

    const pixelRatio = window.devicePixelRatio || 1;
    const displayWidth = Math.max(320, Math.floor(canvas.clientWidth || 900));
    const displayHeight = 180;
    canvas.width = displayWidth * pixelRatio;
    canvas.height = displayHeight * pixelRatio;
    context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    context.clearRect(0, 0, displayWidth, displayHeight);

    context.fillStyle = '#f8fafc';
    context.fillRect(0, 0, displayWidth, displayHeight);

    const samples = buffer.getChannelData(0);
    const middleY = displayHeight / 2;
    const amplitude = displayHeight * 0.38;
    const step = Math.max(1, Math.floor(samples.length / displayWidth));

    context.strokeStyle = '#2563eb';
    context.lineWidth = 1;
    for (let x = 0; x < displayWidth; x += 1) {
        const start = x * step;
        let min = 1;
        let max = -1;
        for (let index = 0; index < step && start + index < samples.length; index += 1) {
            const value = samples[start + index] ?? 0;
            min = Math.min(min, value);
            max = Math.max(max, value);
        }
        context.beginPath();
        context.moveTo(x + 0.5, middleY + min * amplitude);
        context.lineTo(x + 0.5, middleY + max * amplitude);
        context.stroke();
    }

    const selectionStartX = Math.max(0, Math.min(displayWidth, (startSeconds / buffer.duration) * displayWidth));
    const selectionEndX = Math.max(selectionStartX, Math.min(displayWidth, (endSeconds / buffer.duration) * displayWidth));

    context.fillStyle = 'rgba(15, 23, 42, 0.12)';
    context.fillRect(0, 0, selectionStartX, displayHeight);
    context.fillRect(selectionEndX, 0, displayWidth - selectionEndX, displayHeight);
    context.fillStyle = 'rgba(34, 197, 94, 0.16)';
    context.fillRect(selectionStartX, 0, selectionEndX - selectionStartX, displayHeight);

    context.strokeStyle = '#16a34a';
    context.lineWidth = 2;
    context.beginPath();
    context.moveTo(selectionStartX, 0);
    context.lineTo(selectionStartX, displayHeight);
    context.moveTo(selectionEndX, 0);
    context.lineTo(selectionEndX, displayHeight);
    context.stroke();
}

function VoiceCutPanel() {
    const notify = useNotify();
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [sourceFile, setSourceFile] = useState<File | null>(null);
    const [sourceUrl, setSourceUrl] = useState('');
    const [decodedBuffer, setDecodedBuffer] = useState<AudioBuffer | null>(null);
    const [cutStart, setCutStart] = useState(0);
    const [cutEnd, setCutEnd] = useState(0);
    const [cutOutput, setCutOutput] = useState<CutAudioOutput | null>(null);
    const [isDecoding, setIsDecoding] = useState(false);
    const [cutError, setCutError] = useState('');

    useEffect(() => {
        return () => {
            if (sourceUrl) URL.revokeObjectURL(sourceUrl);
        };
    }, [sourceUrl]);

    useEffect(() => {
        if (!canvasRef.current || !decodedBuffer) return undefined;

        const render = () => drawWaveform(canvasRef.current as HTMLCanvasElement, decodedBuffer, cutStart, cutEnd);
        render();
        window.addEventListener('resize', render);
        return () => window.removeEventListener('resize', render);
    }, [decodedBuffer, cutStart, cutEnd]);

    const handleCutFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] ?? null;
        setSourceFile(file);
        setDecodedBuffer(null);
        setCutOutput(null);
        setCutError('');
        setCutStart(0);
        setCutEnd(0);

        if (sourceUrl) {
            URL.revokeObjectURL(sourceUrl);
            setSourceUrl('');
        }

        if (!file) return;

        setSourceUrl(URL.createObjectURL(file));
        setIsDecoding(true);

        try {
            const AudioContextCtor = getAudioContextConstructor();
            if (!AudioContextCtor) {
                throw new Error('Audio editing is unavailable in this browser.');
            }

            const audioContext = new AudioContextCtor();
            const arrayBuffer = await file.arrayBuffer();
            const decoded = await audioContext.decodeAudioData(arrayBuffer.slice(0));
            await audioContext.close();

            setDecodedBuffer(decoded);
            setCutStart(0);
            setCutEnd(Number(decoded.duration.toFixed(2)));
        } catch (decodeError) {
            const message = decodeError instanceof Error ? decodeError.message : 'Could not decode this audio file.';
            setCutError(message);
            notify(message, { type: 'error' });
        } finally {
            setIsDecoding(false);
        }
    };

    const updateRange = (value: number | number[]) => {
        if (!Array.isArray(value)) return;
        const nextStart = Number(value[0] ?? 0);
        const nextEnd = Number(value[1] ?? 0);
        setCutStart(Math.min(nextStart, nextEnd));
        setCutEnd(Math.max(nextStart, nextEnd));
        setCutOutput(null);
    };

    const updateStart = (value: string) => {
        const parsed = Number(value);
        if (!decodedBuffer || Number.isNaN(parsed)) return;
        const nextStart = Math.max(0, Math.min(parsed, cutEnd - 0.01));
        setCutStart(nextStart);
        setCutOutput(null);
    };

    const updateEnd = (value: string) => {
        const parsed = Number(value);
        if (!decodedBuffer || Number.isNaN(parsed)) return;
        const nextEnd = Math.min(decodedBuffer.duration, Math.max(parsed, cutStart + 0.01));
        setCutEnd(nextEnd);
        setCutOutput(null);
    };

    const exportCut = async () => {
        if (!decodedBuffer || !sourceFile) {
            setCutError('Upload an audio file before cutting.');
            return;
        }

        if (cutEnd <= cutStart) {
            setCutError('Choose an end time after the start time.');
            return;
        }

        try {
            const wavBlob = encodeAudioBufferSegmentToWav(decodedBuffer, cutStart, cutEnd);
            const dataUrl = await blobToDataUrl(wavBlob);
            const baseName = sourceFile.name.replace(/\.[^.]+$/, '') || 'audio';
            setCutOutput({
                dataUrl,
                fileName: `${baseName}-cut-${cutStart.toFixed(2)}-${cutEnd.toFixed(2)}.wav`,
                sizeBytes: wavBlob.size,
                durationSeconds: cutEnd - cutStart,
            });
            setCutError('');
            notify('Cut audio is ready to download.', { type: 'success' });
        } catch (cutExportError) {
            const message = cutExportError instanceof Error ? cutExportError.message : 'Could not export the cut audio.';
            setCutError(message);
            notify(message, { type: 'error' });
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 2.5 } }}>
            <Stack spacing={2.5}>
                <Alert severity="info">
                    This cutter runs in your browser. The exported clip is a WAV file and is not uploaded to the server.
                </Alert>
                {isDecoding ? <LinearProgress /> : null}
                {cutError ? <Alert severity="error">{cutError}</Alert> : null}

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.2fr) minmax(320px, 0.8fr)' },
                        gap: 2,
                    }}
                >
                    <Stack component={Paper} variant="outlined" spacing={2} sx={{ p: 2, borderRadius: 2, minWidth: 0 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                            <Button
                                component="label"
                                variant="outlined"
                                startIcon={<UploadFileRoundedIcon />}
                                disabled={isDecoding}
                                sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                            >
                                Upload Audio
                                <input
                                    type="file"
                                    accept="audio/wav,audio/mpeg,audio/mp3,audio/mp4,audio/aac,audio/ogg,audio/webm,audio/flac"
                                    hidden
                                    onChange={handleCutFileChange}
                                />
                            </Button>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 700, color: '#0f172a', overflowWrap: 'anywhere' }}>
                                    {sourceFile?.name ?? 'No audio file selected'}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748b' }}>
                                    {sourceFile ? `${formatBytes(sourceFile.size)} source file` : 'Upload an audio file to see the waveform'}
                                </Typography>
                            </Box>
                        </Stack>

                        {sourceUrl ? (
                            <audio controls src={sourceUrl} style={{ width: '100%' }}>
                                <track kind="captions" />
                            </audio>
                        ) : null}

                        <Box
                            sx={{
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 2,
                                overflow: 'hidden',
                                backgroundColor: '#f8fafc',
                                minHeight: 180,
                            }}
                        >
                            {decodedBuffer ? (
                                <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: 180 }} />
                            ) : (
                                <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 180, px: 2, textAlign: 'center' }}>
                                    <GraphicEqOutlinedIcon color="disabled" sx={{ fontSize: 42, mb: 1 }} />
                                    <Typography color="text.secondary">Waveform preview appears after upload.</Typography>
                                </Stack>
                            )}
                        </Box>

                        {decodedBuffer ? (
                            <Stack spacing={1.5}>
                                <Slider
                                    value={[cutStart, cutEnd]}
                                    onChange={(_event, value) => updateRange(value)}
                                    min={0}
                                    max={Number(decodedBuffer.duration.toFixed(2))}
                                    step={0.01}
                                    valueLabelDisplay="auto"
                                    valueLabelFormat={(value) => formatSeconds(value)}
                                    disableSwap
                                />
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                                        gap: 1.5,
                                    }}
                                >
                                    <TextField
                                        label="Start"
                                        type="number"
                                        value={cutStart.toFixed(2)}
                                        onChange={(event) => updateStart(event.target.value)}
                                        inputProps={{ min: 0, max: cutEnd, step: 0.01 }}
                                        fullWidth
                                    />
                                    <TextField
                                        label="End"
                                        type="number"
                                        value={cutEnd.toFixed(2)}
                                        onChange={(event) => updateEnd(event.target.value)}
                                        inputProps={{ min: cutStart, max: decodedBuffer.duration, step: 0.01 }}
                                        fullWidth
                                    />
                                    <TextField
                                        label="Selected"
                                        value={formatSeconds(cutEnd - cutStart)}
                                        InputProps={{ readOnly: true }}
                                        fullWidth
                                    />
                                </Box>
                                <Button
                                    variant="contained"
                                    startIcon={<ContentCutOutlinedIcon />}
                                    onClick={exportCut}
                                    disabled={isDecoding || cutEnd <= cutStart}
                                    sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                                >
                                    Cut Audio
                                </Button>
                            </Stack>
                        ) : null}
                    </Stack>

                    <Stack component={Paper} variant="outlined" spacing={2} sx={{ p: 2, borderRadius: 2, minWidth: 0 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                            Cut Output
                        </Typography>
                        {cutOutput ? (
                            <Stack spacing={1.5}>
                                <audio controls src={cutOutput.dataUrl} style={{ width: '100%' }}>
                                    <track kind="captions" />
                                </audio>
                                <Button
                                    component="a"
                                    href={cutOutput.dataUrl}
                                    download={cutOutput.fileName}
                                    variant="contained"
                                    startIcon={<DownloadOutlinedIcon />}
                                >
                                    Download Cut
                                </Button>
                                <Stack spacing={0.5}>
                                    <Typography sx={{ fontWeight: 800, overflowWrap: 'anywhere' }}>
                                        {cutOutput.fileName}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                                        {formatSeconds(cutOutput.durationSeconds)} - {formatBytes(cutOutput.sizeBytes)}
                                    </Typography>
                                </Stack>
                            </Stack>
                        ) : (
                            <Alert severity="info">Your cut audio preview and download link will appear here.</Alert>
                        )}
                    </Stack>
                </Box>
            </Stack>
        </Box>
    );
}

export function AudioGeneratePage() {
    const notify = useNotify();
    const [activeTab, setActiveTab] = useState('voice-clone');
    const [settings, setSettings] = useState<AudioGenerateSettings>(() => readSettings());
    const [text, setText] = useState('');
    const [referenceAudio, setReferenceAudio] = useState<File | null>(null);
    const [history, setHistory] = useState<AudioHistoryItem[]>(() => readHistory());
    const [selectedHistoryId, setSelectedHistoryId] = useState<string>('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState('');

    const selectedItem = useMemo(
        () => history.find((item) => item.id === selectedHistoryId) ?? history[0] ?? null,
        [history, selectedHistoryId],
    );

    useEffect(() => {
        writeSettings(settings);
    }, [settings]);

    useEffect(() => {
        if (!selectedHistoryId && history[0]) {
            setSelectedHistoryId(history[0].id);
        }
    }, [history, selectedHistoryId]);

    const updateSetting = (key: keyof AudioGenerateSettings, value: string) => {
        setSettings((current) => ({ ...current, [key]: value }));
    };

    const handleReferenceAudioChange = (event: ChangeEvent<HTMLInputElement>) => {
        setReferenceAudio(event.target.files?.[0] ?? null);
    };

    const saveHistoryItem = (item: AudioHistoryItem) => {
        const nextHistory = [item, ...history].slice(0, maxHistoryItems);
        setHistory(nextHistory);
        setSelectedHistoryId(item.id);

        try {
            writeHistory(nextHistory);
        } catch (storageError) {
            const fallbackHistory = [item];
            setHistory(fallbackHistory);
            setSelectedHistoryId(item.id);
            writeHistory(fallbackHistory);
            notify('Local history was trimmed because the browser storage limit was reached.', { type: 'warning' });
            console.warn(storageError);
        }
    };

    const generateAudio = async () => {
        const trimmedText = text.trim();
        if (!trimmedText) {
            setError('Enter the text to synthesize.');
            return;
        }
        if (!referenceAudio) {
            setError('Upload a reference voice file first.');
            return;
        }

        const formData = new FormData();
        formData.append('text', trimmedText);
        formData.append('voice_id', settings.voiceId.trim() || 'voice_clone');
        formData.append('voice_label', settings.voiceLabel.trim() || 'Voice Clone');
        formData.append('gender_code', settings.genderCode);
        formData.append('language', settings.language.trim() || 'en');
        formData.append('reference_text', settings.referenceText.trim() || trimmedText);
        formData.append('reference_audio_format', getFileExtension(referenceAudio));
        formData.append('reference_audio', referenceAudio);

        const cancelToken = `admin-audio-${Date.now()}`;
        setIsGenerating(true);
        setError('');

        try {
            const response = await fetch(`${trimTrailingSlash(settings.baseUrl)}/tts/file`, {
                method: 'POST',
                headers: {
                    'X-Cancel-Token': cancelToken,
                },
                body: formData,
            });
            const contentType = response.headers.get('content-type') ?? 'audio/wav';

            if (!response.ok) {
                const payload = contentType.includes('application/json') ? await response.json() : await response.text();
                throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));
            }

            const blob = await response.blob();
            const dataUrl = await blobToDataUrl(blob);
            const createdAt = new Date().toISOString();
            const fileName = `voice-clone-${createdAt.replace(/[:.]/g, '-')}.wav`;

            saveHistoryItem({
                id: `${createdAt}-${Math.random().toString(36).slice(2, 8)}`,
                createdAt,
                fileName,
                text: trimmedText,
                voiceLabel: settings.voiceLabel.trim() || settings.voiceId.trim() || 'Voice Clone',
                language: settings.language.trim() || 'en',
                sizeBytes: blob.size,
                mimeType: contentType,
                dataUrl,
            });
            notify('Audio generated and saved to temporary browser history.', { type: 'success' });
        } catch (generationError) {
            const message = generationError instanceof Error ? generationError.message : 'Audio generation failed.';
            setError(message);
            notify(message, { type: 'error' });
        } finally {
            setIsGenerating(false);
        }
    };

    const clearHistory = () => {
        setHistory([]);
        setSelectedHistoryId('');
        if (typeof window !== 'undefined') {
            window.localStorage.removeItem(historyStorageKey);
        }
        notify('Temporary audio history cleared.', { type: 'info' });
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1440, mx: 'auto', width: '100%' }}>
            <Title title="Audio Generate" />
            <Stack spacing={2.5}>
                <Box>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                Audio Generate
                            </Typography>
                            <Typography sx={{ mt: 0.5, color: '#475569' }}>
                                Clone a reference voice, cut uploaded audio, and download the WAV output.
                            </Typography>
                        </Box>
                        <Chip icon={<GraphicEqOutlinedIcon />} label="Voice tools" color="primary" variant="outlined" />
                    </Stack>
                </Box>

                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <Tabs
                        value={activeTab}
                        onChange={(_event, value: string) => setActiveTab(value)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{ px: 1, borderBottom: 1, borderColor: 'divider' }}
                    >
                        <Tab value="voice-clone" icon={<AudiotrackOutlinedIcon />} iconPosition="start" label="Voice Clone" />
                        <Tab value="voice-cut" icon={<ContentCutOutlinedIcon />} iconPosition="start" label="Voice Cut" />
                    </Tabs>

                    {activeTab === 'voice-clone' ? (
                        <Box sx={{ p: { xs: 2, md: 2.5 } }}>
                            <Stack spacing={2.5}>
                                <Alert severity="warning">
                                    Generated audio history is stored only in this browser localStorage. It is temporary, device-specific, and can disappear when storage is cleared or the quota is reached.
                                </Alert>

                                {isGenerating ? <LinearProgress /> : null}
                                {error ? <Alert severity="error">{error}</Alert> : null}

                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.25fr) minmax(320px, 0.75fr)' },
                                        gap: 2,
                                    }}
                                >
                                    <Stack component={Paper} variant="outlined" spacing={2} sx={{ p: 2, borderRadius: 2, minWidth: 0 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                            Voice Clone Request
                                        </Typography>

                                        <TextField
                                            label="Text"
                                            value={text}
                                            onChange={(event) => setText(event.target.value)}
                                            multiline
                                            minRows={5}
                                            placeholder="Enter the text to generate in the cloned voice"
                                            fullWidth
                                            disabled={isGenerating}
                                        />

                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                                            <Button
                                                component="label"
                                                variant="outlined"
                                                startIcon={<UploadFileRoundedIcon />}
                                                disabled={isGenerating}
                                                sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                                            >
                                                Upload Voice
                                                <input
                                                    type="file"
                                                    accept="audio/wav,audio/mpeg,audio/mp3,audio/x-wav"
                                                    hidden
                                                    onChange={handleReferenceAudioChange}
                                                />
                                            </Button>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography sx={{ fontWeight: 700, color: '#0f172a', overflowWrap: 'anywhere' }}>
                                                    {referenceAudio?.name ?? 'No voice file selected'}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: '#64748b' }}>
                                                    WAV or MP3 reference audio
                                                </Typography>
                                            </Box>
                                        </Stack>

                                        <Divider />

                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                                                gap: 1.5,
                                            }}
                                        >
                                            <TextField
                                                label="Base URL"
                                                value={settings.baseUrl}
                                                onChange={(event) => updateSetting('baseUrl', event.target.value)}
                                                disabled={isGenerating}
                                                fullWidth
                                            />
                                            <TextField
                                                label="Language"
                                                value={settings.language}
                                                onChange={(event) => updateSetting('language', event.target.value)}
                                                disabled={isGenerating}
                                                fullWidth
                                            />
                                            <TextField
                                                label="Voice ID"
                                                value={settings.voiceId}
                                                onChange={(event) => updateSetting('voiceId', event.target.value)}
                                                disabled={isGenerating}
                                                fullWidth
                                            />
                                            <TextField
                                                label="Voice Label"
                                                value={settings.voiceLabel}
                                                onChange={(event) => updateSetting('voiceLabel', event.target.value)}
                                                disabled={isGenerating}
                                                fullWidth
                                            />
                                            <TextField
                                                select
                                                label="Gender"
                                                value={settings.genderCode}
                                                onChange={(event) => updateSetting('genderCode', event.target.value)}
                                                disabled={isGenerating}
                                                fullWidth
                                            >
                                                <MenuItem value="male">Male</MenuItem>
                                                <MenuItem value="female">Female</MenuItem>
                                                <MenuItem value="neutral">Neutral</MenuItem>
                                            </TextField>
                                            <TextField
                                                label="Reference Text"
                                                value={settings.referenceText}
                                                onChange={(event) => updateSetting('referenceText', event.target.value)}
                                                disabled={isGenerating}
                                                fullWidth
                                            />
                                        </Box>

                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                                            <Button
                                                variant="contained"
                                                startIcon={<PlayArrowRoundedIcon />}
                                                onClick={generateAudio}
                                                disabled={isGenerating}
                                            >
                                                Generate Audio
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                color="inherit"
                                                startIcon={<DeleteOutlineIcon />}
                                                onClick={clearHistory}
                                                disabled={isGenerating || history.length === 0}
                                            >
                                                Clear History
                                            </Button>
                                        </Stack>
                                    </Stack>

                                    <Stack component={Paper} variant="outlined" spacing={2} sx={{ p: 2, borderRadius: 2, minWidth: 0 }}>
                                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                            <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                                Temporary History
                                            </Typography>
                                            <Chip size="small" icon={<HistoryOutlinedIcon />} label={`${history.length}/${maxHistoryItems}`} />
                                        </Stack>

                                        {selectedItem ? (
                                            <Stack spacing={1.5}>
                                                <audio controls src={selectedItem.dataUrl} style={{ width: '100%' }}>
                                                    <track kind="captions" />
                                                </audio>
                                                <Button
                                                    component="a"
                                                    href={selectedItem.dataUrl}
                                                    download={buildDownloadName(selectedItem)}
                                                    variant="contained"
                                                    startIcon={<DownloadOutlinedIcon />}
                                                >
                                                    Download Audio
                                                </Button>
                                                <Stack spacing={0.5}>
                                                    <Typography sx={{ fontWeight: 800, overflowWrap: 'anywhere' }}>
                                                        {selectedItem.voiceLabel}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                                                        {formatDateTime(selectedItem.createdAt)} - {selectedItem.language} - {formatBytes(selectedItem.sizeBytes)}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: '#334155', overflowWrap: 'anywhere' }}>
                                                        {selectedItem.text}
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                        ) : (
                                            <Alert severity="info">Generated audio will appear here after the first successful request.</Alert>
                                        )}

                                        <Divider />

                                        <Stack spacing={1}>
                                            {history.map((item) => {
                                                const selected = item.id === selectedItem?.id;
                                                return (
                                                    <Button
                                                        key={item.id}
                                                        variant={selected ? 'contained' : 'outlined'}
                                                        color={selected ? 'primary' : 'inherit'}
                                                        onClick={() => setSelectedHistoryId(item.id)}
                                                        sx={{
                                                            justifyContent: 'flex-start',
                                                            textAlign: 'left',
                                                            minHeight: 56,
                                                            px: 1.5,
                                                        }}
                                                    >
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Typography sx={{ fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {item.voiceLabel || 'Voice Clone'}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ display: 'block', opacity: 0.82, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {formatDateTime(item.createdAt)} - {item.text}
                                                            </Typography>
                                                        </Box>
                                                    </Button>
                                                );
                                            })}
                                        </Stack>
                                    </Stack>
                                </Box>
                            </Stack>
                        </Box>
                    ) : null}

                    {activeTab === 'voice-cut' ? <VoiceCutPanel /> : null}
                </Paper>
            </Stack>
        </Box>
    );
}
